<template>
    <div>
        <!-- Preloader -->
        <PagePreloader v-if="!pageIsLoaded"></PagePreloader>

        <!-- Remove Campaign Modal -->
        <b-modal
            size="lg"
            ref="delete-account-modal"
            id="delete-account-modal"
            centered
            title="Are you sure you want to stop this Campaign?"
        >
            <!-- Error management -->
            <CustomAlerts :errors="modalerrors"></CustomAlerts>

            <div v-if="campaign.account_data">
                <div v-if="!canDeleteCampaign">
                    <InfoBox
                        text="In order to avoid abusing the system Campaigns have to run for a minimum of 5 days before you can delete them. Try again later."
                    ></InfoBox>
                </div>
                <div v-else>
                    Your Campaign will be halted for the following user:
                    <code> @{{ campaign.account_data.username }}</code
                    >.
                </div>
            </div>

            <template v-slot:modal-footer="{ ok, cancel, hide }" v-if="campaign.account_data">
                <b-button
                    type="submit"
                    variant="danger"
                    :disabled="modalIsLoading"
                    @click="stopCampaign"
                    v-if="canDeleteCampaign"
                >
                    Stop Campaign
                </b-button>
                <!-- Button with custom close trigger value -->
                <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
            </template>
        </b-modal>

        <!-- Add Campaign modal -->
        <b-modal
            size="lg"
            ref="add-account-modal"
            id="add-account-modal"
            centered
            title="Add new Campaign"
        >
            <!-- Preloader -->
            <PagePreloader v-if="modalIsLoading"></PagePreloader>

            <!-- Error management -->
            <CustomAlerts :errors="modalerrors"></CustomAlerts>

            <div v-if="canAddCampaigns">
                <form method="POST" ref="addInstagramAccountForm" v-on:submit.prevent="addCampaign">
                    <b-form-group
                        label-cols="4"
                        label-cols-lg="3"
                        label="Instagram Account"
                        label-for="input-default"
                    >
                        <b-form-input
                            placeholder="Enter the username for the Instagram Account you want to add"
                            v-model="formAddAccount"
                        >
                        </b-form-input>
                    </b-form-group>
                    <b-button
                        type="Submit"
                        class="d-none"
                        :disabled="modalIsLoading"
                        @click="addCampaign"
                    ></b-button>
                </form>
            </div>
            <div v-else>
                <InfoBox
                    text="You've currently running your maximum number of Hashtag Performance Campaigns. 
            Upgrade your Subscription in order to run additional simultaneous Hashtag Performance Campaigns!"
                >
                </InfoBox>
                <b-button to="/subscribe" variant="success">Upgrade Now</b-button>
            </div>

            <template v-slot:modal-footer="{ ok, cancel, hide }">
                <b-button
                    type="submit"
                    variant="success"
                    @click="addCampaign"
                    :disabled="modalIsLoading"
                    v-if="canAddCampaigns"
                >
                    Start Campaign
                </b-button>
                <!-- Button with custom close trigger value -->
                <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
            </template>
        </b-modal>
        <!-- End add Campaign modal -->

        <div class="row">
            <div class="col-xl-3 d-print-none">
                <div class="card card-custom card-stretch gutter-b">
                    <div class="card-header">
                        <div class="card-title">
                            <h3 class="card-label">Active Campaigns</h3>
                        </div>
                    </div>
                    <div class="card-body pt-4">
                        <div>
                            <div class="d-flex align-items-center rounded pl-5 pt-5 pb-5">
                                <span class="svg-icon svg-icon-warning mr-5">
                                    <i class="flaticon-pie-chart-1 text-success"></i>
                                </span>
                                <div class="d-flex flex-column flex-grow-1 mr-2">
                                    <a
                                        class="
                                            font-weight-bold
                                            text-dark-75 text-hover-primary
                                            font-size-md
                                            mb-1
                                        "
                                        >Active Campaigns</a
                                    >
                                </div>
                                <span class="font-weight-bolder text-success py-1 font-size-lg">
                                    {{ mycampaigns.length }}/{{ currentUser.campaigns }}
                                </span>
                            </div>
                            <div class="separator separator-dashed separator-border-1"></div>
                        </div>
                        <div class="navi navi-bold navi-hover navi-active navi-link-rounded">
                            <template v-if="isFedor">
                                <b-form-select
                                    v-model="sortCampaignsMode"
                                    :options="sortCampaignsOptions"
                                    size="sm"
                                    class="mb-3 mt-3"
                                    @change="handleSortCampaignsChange()"
                                >
                                </b-form-select>
                            </template>
                            <div v-for="campaign in mycampaigns" :key="campaign.id">
                                <div class="navi-item">
                                    <b-button
                                        variant="white"
                                        @click="getCampaign(campaign)"
                                        class="navi-link py-4 campaign-link text-left w-100"
                                        :disabled="postsAreLoading"
                                        :class="
                                            isCurrentCampaign(campaign)
                                                ? 'campaign-link-active'
                                                : ''
                                        "
                                    >
                                        <span class="navi-icon mr-2">
                                            <span class="svg-icon">
                                                <b-img
                                                    v-bind:src="campaign.profilepic"
                                                    rounded
                                                    fluid
                                                ></b-img>
                                            </span>
                                        </span>
                                        <span
                                            class="navi-text font-size-lg"
                                            :class="
                                                isCurrentCampaign(campaign) ? 'text-success' : ''
                                            "
                                        >
                                            {{ campaign.username }}
                                            <span class="navi-text font-size-sm text-muted d-block">
                                                {{ campaign.followers_pretty }}
                                                followers
                                            </span>
                                            <template v-if="isFedor">
                                                <span
                                                    class="
                                                        navi-text
                                                        font-size-sm
                                                        text-muted
                                                        d-block
                                                    "
                                                >
                                                    {{ campaign.days_since_start }}
                                                    days old
                                                </span>
                                            </template>
                                        </span>
                                    </b-button>
                                    <div
                                        class="separator separator-dashed separator-border-1"
                                    ></div>
                                </div>
                            </div>
                            <b-button
                                variant="success"
                                class="col-12 gutter-t"
                                @click="openNewCampaignModal"
                                >Add new Campaign</b-button
                            >
                        </div>
                    </div>
                </div>
            </div>

            <!-- Open col 9 -->
            <div class="col-xl-9">
                <!-- Print header -->
                <EnterpriseLogoDisplay>
                    <div class="gutter-b">
                        <h3>Hashtastic Performance Report</h3>
                        <h6>
                            <sub>Generated on {{ Date() }}</sub>
                        </h6>
                    </div>
                </EnterpriseLogoDisplay>

                <!-- Preloader -->
                <PagePreloader v-if="campaignIsLoading">campaign</PagePreloader>

                <!-- Error management -->
                <CustomAlerts :errors="errors"></CustomAlerts>

                <div class="card card-custom gutter-b" v-if="!displayCampaign">
                    <div class="card-header">
                        <div class="card-title">
                            <span class="card-icon">
                                <i class="flaticon2-chat-1 text-primary"></i>
                            </span>
                            <h3 class="card-label">My Campaigns</h3>
                        </div>
                    </div>
                    <div class="card-body">
                        <p class="lead">Welcome to your Hashtag Performance Campaigns.</p>
                    </div>
                </div>

                <div v-if="displayCampaign">
                    <!-- Display link for Fedor -->
                    <template v-if="currentUser.id == 26410">
                        <b-button
                            class="col-12 gutter-b"
                            variant="success"
                            :href="'https://analytics.yellowdd.com/?campaign=' + campaign.accountid"
                            target="_blank"
                            >External Analytics Link</b-button
                        >
                    </template>
                    <!-- Display badge -->
                    <AccountBadgeWide :account="campaign.account_data">
                        <template v-slot:actions>
                            <div class="my-lg-0 my-1 d-print-none">
                                <b-button-group>
                                    <button
                                        v-if="isEnterprise"
                                        type="button"
                                        class="
                                            btn btn-light-primary
                                            font-weight-bolder
                                            text-uppercase
                                        "
                                        @click="printReport(campaign.account_data.userid)"
                                    >
                                        <i class="fas fa-download ml-2"></i> Export
                                    </button>
                                    <b-button
                                        @click="viewGrowth(campaign.account_data.username)"
                                        class="
                                            btn btn-sm btn-light-success
                                            font-weight-bolder
                                            text-uppercase
                                        "
                                    >
                                        Growth
                                    </b-button>
                                    <b-button
                                        @click="openStopCampaignModal"
                                        class="
                                            btn btn-sm btn-light-danger
                                            font-weight-bolder
                                            text-uppercase
                                            mr-2
                                        "
                                        >Stop Campaign</b-button
                                    >
                                </b-button-group>
                            </div>
                        </template>
                    </AccountBadgeWide>

                    <div class="card card-custom gutter-b">
                        <div class="card-header">
                            <div class="card-title">
                                <span class="card-icon">
                                    <i class="fas fa-check-circle text-success"></i>
                                </span>
                                <h3 class="card-label">Campaign Results</h3>
                            </div>
                        </div>
                        <div class="card-body pb-0 pt-0">
                            <!-- View version -->

                            <div class="row">
                                <div class="col-sm-12 col-md-3 card-border-right result-card">
                                    <div class="row no-gutters h-100">
                                        <div class="col-10">
                                            <h4 class="result-title">Followers</h4>
                                            <p class="result-desc">Increase in follower count</p>
                                        </div>
                                        <div class="col-2 my-auto">
                                            <h4 class="text-success result-value">
                                                {{ campaign.progress.followerdelta }}
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-sm-12 col-md-3 card-border-right result-card">
                                    <div class="row no-gutters h-100">
                                        <div class="col-10">
                                            <h4 class="result-title">New Posts</h4>
                                            <p class="result-desc">Tracked by Hashtastic</p>
                                        </div>
                                        <div class="col-2 my-auto">
                                            <h4 class="text-success result-value">
                                                {{ campaign.progress.trackedpostcount }}
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-sm-12 col-md-3 card-border-right result-card">
                                    <div class="row no-gutters h-100">
                                        <div class="col-10">
                                            <h4 class="result-title">Hashtag Performance</h4>
                                            <p class="result-desc">
                                                Number of hashtags you ranked on
                                            </p>
                                        </div>
                                        <div class="col-2 my-auto">
                                            <h4 class="text-success result-value">
                                                {{
                                                    campaign.performance.statistics
                                                        .totalrankedhashtags
                                                }}
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-sm-12 col-md-3 result-card">
                                    <div class="row no-gutters h-100">
                                        <div class="col-10">
                                            <h4 class="result-title">Days since start</h4>
                                            <p class="result-desc">
                                                Campaign running since
                                                {{ campaign.progress.startdate }}
                                            </p>
                                        </div>
                                        <div class="col-2 my-auto">
                                            <h4 class="text-success result-value">
                                                {{ campaign.progress.dayssincestart }}
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="card card-custom gutter-b d-print-none"
                        v-if="campaign.performance.individual_hashtag_performance.length > 0"
                    >
                        <div class="card-header">
                            <div class="card-title">
                                <span class="card-icon">
                                    <i class="fas fa-hashtag text-success"></i>
                                </span>
                                <h3 class="card-label">Selected Hashtags</h3>
                            </div>
                            <div class="card-toolbar">
                                <span
                                    class="btn btn-bold btn-sm"
                                    :class="
                                        selectedHashtags.length >= 30
                                            ? 'btn-outline-danger'
                                            : 'btn-outline-primary'
                                    "
                                >
                                    {{ selectedHashtags.length }} hashtags selected
                                </span>
                            </div>
                        </div>
                        <div class="card-body">
                            <div v-if="selectedHashtags.length < 1">
                                <infoBox
                                    text="You currently haven't selected any hashtags. Use the tables below to make your selection."
                                ></infoBox>
                            </div>

                            <div v-if="selectedHashtags.length > 0">
                                <div class="row">
                                    <div class="col-xl-12 btn-group" style="flex-wrap: wrap">
                                        <div v-for="selectedHashtag in selectedHashtags">
                                            <span
                                                class="
                                                    btn btn-sm btn-font-sm
                                                    font-weight-bold
                                                    btn-light-primary
                                                    m-1
                                                "
                                                >#{{ selectedHashtag }}</span
                                            >
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="card-footer row" v-if="selectedHashtags.length > 0">
                            <!-- NOTE - Copy to Clipboard button -->
                            <buttonCopyToClipboard
                                :propHashtags="selectedHashtagsHashed"
                                class="col-md-3 col-12"
                            ></buttonCopyToClipboard>
                            <!-- NOTE - Create new hashtag set -->
                            <buttonCreateNewHashtagSet
                                :propHashtags="selectedHashtagsHashed"
                                @emitPassMessage="handleEmitMessages"
                                class="col-md-3 col-12"
                            >
                            </buttonCreateNewHashtagSet>
                            <!-- NOTE - Add to existing hashtag set -->
                            <buttonAddToExistingHashtagSet
                                @emitPassMessage="handleEmitMessages"
                                :propHashtags="selectedHashtagsHashed"
                                class="col-md-3 col-12"
                            ></buttonAddToExistingHashtagSet>
                        </div>
                    </div>

                    <div class="card card-custom gutter-b">
                        <div class="card-header">
                            <div class="card-title">
                                <span class="card-icon">
                                    <i class="fas fa-tachometer-alt text-success"></i>
                                </span>
                                <h3 class="card-label">Performance Summary</h3>
                            </div>
                        </div>
                        <div class="card-body pt-0">
                            <div class="">
                                <div class="row">
                                    <div
                                        v-if="
                                            campaign.performance.individual_hashtag_performance
                                                .length > 0
                                        "
                                        class="col-12"
                                    >
                                        <b-tabs
                                            pills
                                            content-class="mt-3"
                                            class="w-100 mt-5"
                                            v-model="summaryTabIndex"
                                        >
                                            <b-tab title="Summary" title-item-class="mytab" active>
                                                <div class="card card-custom">
                                                    <div class="card-body">
                                                        <div class="row">
                                                            <div class="col-sm-12 col-md-6">
                                                                <div id="chart">
                                                                    <h4>Hashtag Performance</h4>
                                                                    <h6 class="text-muted">
                                                                        Hashtag Performance by
                                                                        section
                                                                    </h6>
                                                                    <apexchart
                                                                        type="donut"
                                                                        :options="
                                                                            campaignChartOptions
                                                                        "
                                                                        :series="
                                                                            campaignChartSeries
                                                                        "
                                                                        class="p-2"
                                                                    ></apexchart>
                                                                </div>
                                                            </div>
                                                            <div class="col-sm-12 col-md-6">
                                                                <div
                                                                    class="
                                                                        alert
                                                                        alert-custom
                                                                        alert-white
                                                                        shadow-sm
                                                                        alert-elevate
                                                                    "
                                                                    role="alert"
                                                                >
                                                                    <div class="alert-icon">
                                                                        <i
                                                                            class="
                                                                                fas
                                                                                fa-fire-alt
                                                                                text-success
                                                                            "
                                                                        ></i>
                                                                    </div>
                                                                    <div class="alert-text">
                                                                        You've ranked on
                                                                        <span
                                                                            class="
                                                                                font-weight-bolder
                                                                            "
                                                                            >{{
                                                                                campaign.performance
                                                                                    .statistics
                                                                                    .totalrankedhashtags
                                                                            }}
                                                                            hashtags</span
                                                                        >
                                                                        you've used since the
                                                                        <span
                                                                            class="
                                                                                font-weight-bolder
                                                                            "
                                                                            >{{
                                                                                campaign.progress
                                                                                    .startdate
                                                                            }}</span
                                                                        >, including
                                                                        <span
                                                                            class="
                                                                                font-weight-bolder
                                                                            "
                                                                            >{{
                                                                                campaign.performance
                                                                                    .statistics
                                                                                    .totaltop9hashtags
                                                                            }}
                                                                            hashtags</span
                                                                        >
                                                                        that ranked in the top 9
                                                                        positions.
                                                                    </div>
                                                                </div>
                                                                <b-button
                                                                    @click="summaryTabIndex++"
                                                                    variant="light-success"
                                                                    class="
                                                                        col-12 col-xl-6
                                                                        d-print-none
                                                                    "
                                                                    >View all ranked
                                                                    hashtags</b-button
                                                                >
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </b-tab>
                                            <b-tab
                                                title="Individual Hashtag Performance"
                                                title-item-class="mytab"
                                            >
                                                <InfoBox
                                                    text="This table indicates the number of times your posts ranked within the 'top posts' section of a hashtag feed, as well as how well they ranked."
                                                ></InfoBox>

                                                <!-- Select / Deselect toolbar -->
                                                <div class="card card-custom">
                                                    <div class="card-header">
                                                        <div class="card-title"></div>
                                                        <div class="card-toolbar d-print-none">
                                                            <div class="btn-group">
                                                                <b-button
                                                                    variant="light-primary"
                                                                    size="sm"
                                                                    @click="selectAll('summary')"
                                                                    >Select All</b-button
                                                                >
                                                                <b-button
                                                                    variant="light-danger"
                                                                    size="sm"
                                                                    @click="deselectAll('summary')"
                                                                    >Deselect All</b-button
                                                                >
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Table -->
                                                <b-table
                                                    striped
                                                    hover
                                                    selectable
                                                    bordered
                                                    select-mode="multi"
                                                    @row-clicked="
                                                        (item) => onRowClicked('summary', item)
                                                    "
                                                    :fields="fieldsSummary"
                                                    :items="
                                                        campaign.performance
                                                            .individual_hashtag_performance
                                                    "
                                                    :per-page="15"
                                                    :current-page="summaryCurrentPage"
                                                    sortBy="top9count"
                                                    :sortDesc="true"
                                                    :sort-compare="customTableSort"
                                                    responsive="sm"
                                                    ref="selectableTable"
                                                >
                                                    <template
                                                        v-slot:cell(selected)="{
                                                            item,
                                                            field: { key },
                                                        }"
                                                    >
                                                        <template v-if="item[key]">
                                                            <span aria-hidden="true"
                                                                ><i
                                                                    class="
                                                                        far
                                                                        fa-check-square
                                                                        text-primary
                                                                        font-weight-bolder
                                                                    "
                                                                ></i
                                                            ></span>
                                                            <span class="sr-only">Selected</span>
                                                        </template>
                                                        <template v-else>
                                                            <span aria-hidden="true"
                                                                ><i class="far fa-square"></i
                                                            ></span>
                                                            <span class="sr-only"
                                                                >Not selected</span
                                                            >
                                                        </template>
                                                        <!-- <b-checkbox v-model="item[key]"></b-checkbox> -->
                                                    </template>

                                                    <!-- Hashtag badge slot -->
                                                    <template
                                                        v-slot:cell(hashtagsBadged)="{
                                                            item,
                                                            field: { key },
                                                        }"
                                                    >
                                                        {{ item.hashtag }}
                                                        <span
                                                            class="badge badge-warning"
                                                            v-if="item.status == 'flagged'"
                                                            >Flagged</span
                                                        >
                                                        <span
                                                            class="badge badge-danger"
                                                            v-if="item.status == 'banned'"
                                                            >Banned</span
                                                        >
                                                    </template>
                                                </b-table>
                                                <b-pagination
                                                    v-model="summaryCurrentPage"
                                                    :total-rows="
                                                        campaign.performance
                                                            .individual_hashtag_performance.length
                                                    "
                                                    :per-page="15"
                                                    align="center"
                                                    aria-controls="my-table"
                                                ></b-pagination>
                                            </b-tab>
                                        </b-tabs>
                                    </div>
                                    <div v-else class="col-12">
                                        <InfoBox
                                            class="mt-10"
                                            text="There are no hashtags currently being tracked by Hashtastic for this account. Results will appear here as soon as you start posting on Instagram!"
                                        ></InfoBox>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="displayPosts">
                        <!-- CASE - No posts to display -->
                        <template v-if="campaignposts.length < 1">
                            <div class="card card-custom card-body">
                                <p>
                                    There are currently no posts being tracked by Hashtastic for
                                    this user. If you've added this account recently, please note
                                    that older posts won't appear here, only newer posts. As soon as
                                    you start posting to Instagram, Hashtastic will start logging
                                    their performance here.
                                </p>
                            </div>
                        </template>
                        <!-- CASE - Display posts -->
                        <template v-for="(campaignpost, postIndex) in campaignposts">
                            <div
                                class="card card-custom gutter-b rows-print-as-pages"
                                :key="campaignpost.id"
                            >
                                <div class="card-body">
                                    <!--begin::Top-->
                                    <div class="d-flex">
                                        <!--begin::Pic-->
                                        <div class="flex-shrink-0 mr-7">
                                            <div class="symbol symbol-50 symbol-lg-120">
                                                <b-img
                                                    v-bind:src="campaignpost.thumbnail"
                                                    rounded
                                                    fluid
                                                ></b-img>
                                            </div>
                                        </div>
                                        <!--end::Pic-->
                                        <!--begin: Info-->
                                        <div class="flex-grow-1">
                                            <!--begin::Title-->
                                            <div
                                                class="
                                                    d-flex
                                                    align-items-center
                                                    justify-content-between
                                                    flex-wrap
                                                    mt-2
                                                "
                                            >
                                                <!--begin::User-->
                                                <div class="mr-3">
                                                    <!--begin::Name-->
                                                    <a
                                                        class="
                                                            d-flex
                                                            align-items-center
                                                            text-dark text-hover-primary
                                                            font-size-h5 font-weight-bold
                                                            mr-3
                                                        "
                                                    >
                                                        {{ campaignpost.nice_type }}
                                                        Post
                                                        <span
                                                            class="
                                                                label
                                                                label-light-success
                                                                label-inline
                                                                ml-2
                                                            "
                                                            >{{ campaignpost.nice_date }}</span
                                                        >
                                                    </a>
                                                    <!--end::Name-->
                                                    <!--begin::Contacts-->
                                                    <div class="d-flex flex-wrap my-2">
                                                        <a
                                                            class="
                                                                text-muted text-hover-primary
                                                                font-weight-bold
                                                                mr-lg-8 mr-5
                                                                mb-lg-0 mb-2
                                                            "
                                                        >
                                                            <span
                                                                class="
                                                                    svg-icon
                                                                    svg-icon-md
                                                                    svg-icon-gray-500
                                                                    mr-1
                                                                "
                                                            >
                                                                <!--begin::Svg Icon | path:assets/media/svg/icons/Communication/Mail-notification.svg-->
                                                                <i class="flaticon2-new-email"></i>
                                                                <!--end::Svg Icon-->
                                                            </span>
                                                            {{ campaignpost.shortcode }}
                                                        </a>
                                                        <a
                                                            class="
                                                                text-muted text-hover-primary
                                                                font-weight-bold
                                                                mr-lg-8 mr-5
                                                                mb-lg-0 mb-2
                                                            "
                                                        >
                                                            <span
                                                                class="
                                                                    svg-icon
                                                                    svg-icon-md
                                                                    svg-icon-gray-500
                                                                    mr-1
                                                                "
                                                            >
                                                                <!--begin::Svg Icon | path:assets/media/svg/icons/Communication/Mail-notification.svg-->
                                                                <i class="flaticon-clock-2"></i>
                                                                <!--end::Svg Icon--> </span
                                                            >Posted
                                                            {{ campaignpost.posted_ago }}
                                                            days ago
                                                        </a>
                                                        <a
                                                            class="
                                                                text-muted text-hover-primary
                                                                font-weight-bold
                                                                mr-lg-8 mr-5
                                                                mb-lg-0 mb-2
                                                            "
                                                            v-if="campaignpost.location"
                                                        >
                                                            <span
                                                                class="
                                                                    svg-icon
                                                                    svg-icon-md
                                                                    svg-icon-gray-500
                                                                    mr-1
                                                                "
                                                            >
                                                                <!--begin::Svg Icon | path:assets/media/svg/icons/Communication/Mail-notification.svg-->
                                                                <i
                                                                    class="flaticon2-placeholder"
                                                                ></i>
                                                                <!--end::Svg Icon--> </span
                                                            >{{ campaignpost.location }}
                                                        </a>
                                                    </div>
                                                    <!--end::Contacts-->
                                                </div>
                                                <!--begin::User-->
                                            </div>
                                            <!--end::Title-->
                                        </div>
                                        <!--end::Info-->

                                        <div class="card-toolbar">
                                            <b-dropdown
                                                size="sm"
                                                no-caret
                                                variant="hover-light-primary"
                                                class="
                                                    m-2
                                                    btn btn-hover-light-primary btn-icon btn-sm
                                                    d-print-none
                                                "
                                            >
                                                <template v-slot:button-content>
                                                    <i class="ki ki-bold-more-hor"></i>
                                                </template>
                                                <b-dropdown-item
                                                    :href="
                                                        'https://instagram.com/p/' +
                                                        campaignpost.shortcode
                                                    "
                                                    target="_blank"
                                                    ><i class="flaticon-instagram-logo mr-2"></i>
                                                    View on Instagram</b-dropdown-item
                                                >
                                            </b-dropdown>
                                        </div>
                                    </div>
                                    <!--end::Top-->
                                    <!--begin::Separator-->
                                    <div class="separator separator-solid my-7"></div>
                                    <!--end::Separator-->
                                    <!--begin::Bottom-->
                                    <div class="d-flex align-items-center flex-wrap">
                                        <b-tabs pills content-class="mt-3" class="w-100">
                                            <b-tab
                                                title="Hashtags you ranked on"
                                                title-item-class="mytab"
                                                active
                                            >
                                                <div v-if="campaignpost.rank_data.length > 0">
                                                    <InfoBox
                                                        text="This table indicates the position your post ranked within the 'top posts' section of a hashtag feed."
                                                    ></InfoBox>

                                                    <!-- Select / Deselect toolbar -->
                                                    <div class="card card-custom">
                                                        <div class="card-header">
                                                            <div class="card-title"></div>
                                                            <div class="card-toolbar d-print-none">
                                                                <div class="btn-group">
                                                                    <b-button
                                                                        variant="light-primary"
                                                                        size="sm"
                                                                        @click="
                                                                            selectAll(
                                                                                'ranked',
                                                                                postIndex
                                                                            )
                                                                        "
                                                                        >Select All</b-button
                                                                    >
                                                                    <b-button
                                                                        variant="light-danger"
                                                                        size="sm"
                                                                        @click="
                                                                            deselectAll(
                                                                                'ranked',
                                                                                postIndex
                                                                            )
                                                                        "
                                                                        >Deselect All</b-button
                                                                    >
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <!-- Table -->
                                                    <b-table
                                                        striped
                                                        hover
                                                        selectable
                                                        bordered
                                                        select-mode="multi"
                                                        @row-clicked="
                                                            (item) =>
                                                                onRowClicked(
                                                                    campaignpost.postid + 'ranked',
                                                                    item
                                                                )
                                                        "
                                                        :fields="fields"
                                                        :items="campaignpost.rank_data"
                                                        :per-page="15"
                                                        :current-page="
                                                            campaignpost.currentPageRanked
                                                        "
                                                        sortBy="minrank"
                                                        :sort-compare="customTableSort"
                                                        responsive="sm"
                                                        ref="selectableTable"
                                                    >
                                                        <template
                                                            v-slot:cell(selected)="{
                                                                item,
                                                                field: { key },
                                                            }"
                                                        >
                                                            <template v-if="item[key]">
                                                                <span aria-hidden="true"
                                                                    ><i
                                                                        class="
                                                                            far
                                                                            fa-check-square
                                                                            text-primary
                                                                            font-weight-bolder
                                                                        "
                                                                    ></i
                                                                ></span>
                                                                <span class="sr-only"
                                                                    >Selected</span
                                                                >
                                                            </template>
                                                            <template v-else>
                                                                <span aria-hidden="true"
                                                                    ><i class="far fa-square"></i
                                                                ></span>
                                                                <span class="sr-only"
                                                                    >Not selected</span
                                                                >
                                                            </template>
                                                            <!-- <b-checkbox v-model="item[key]"></b-checkbox> -->
                                                        </template>

                                                        <!-- Hashtag badge slot -->
                                                        <template
                                                            v-slot:cell(hashtagsBadged)="{
                                                                item,
                                                                field: { key },
                                                            }"
                                                        >
                                                            {{ item.hashtag }}
                                                            <span
                                                                class="badge badge-warning"
                                                                v-if="item.status == 'flagged'"
                                                                >Flagged</span
                                                            >
                                                            <span
                                                                class="badge badge-danger"
                                                                v-if="item.status == 'banned'"
                                                                >Banned</span
                                                            >
                                                        </template>
                                                    </b-table>
                                                    <b-pagination
                                                        v-model="campaignpost.currentPageRanked"
                                                        :total-rows="campaignpost.rank_data.length"
                                                        :per-page="15"
                                                        align="center"
                                                        aria-controls="my-table"
                                                    ></b-pagination>
                                                </div>
                                                <div v-else>
                                                    <InfoBox
                                                        text="This post hasn't yet ranked on any hashtags."
                                                    ></InfoBox>
                                                </div>
                                            </b-tab>

                                            <b-tab
                                                title="Unranked hashtags"
                                                title-item-class="mytab"
                                            >
                                                <div v-if="campaignpost.unranked.length > 0">
                                                    <InfoBox
                                                        text="This table indicates the instances where your post didn't rank in the 'top posts' section of a hashtag feed."
                                                    ></InfoBox>

                                                    <!-- Select / Deselect toolbar -->
                                                    <div class="card card-custom">
                                                        <div class="card-header">
                                                            <div class="card-title"></div>
                                                            <div class="card-toolbar d-print-none">
                                                                <div class="btn-group">
                                                                    <b-button
                                                                        variant="light-primary"
                                                                        size="sm"
                                                                        @click="
                                                                            selectAll(
                                                                                'unranked',
                                                                                postIndex
                                                                            )
                                                                        "
                                                                        >Select All</b-button
                                                                    >
                                                                    <b-button
                                                                        variant="light-danger"
                                                                        size="sm"
                                                                        @click="
                                                                            deselectAll(
                                                                                'unranked',
                                                                                postIndex
                                                                            )
                                                                        "
                                                                        >Deselect All</b-button
                                                                    >
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <!-- Table -->
                                                    <b-table
                                                        striped
                                                        hover
                                                        selectable
                                                        bordered
                                                        select-mode="multi"
                                                        @row-clicked="
                                                            (item) =>
                                                                onRowClicked(
                                                                    campaignpost.postid +
                                                                        'unranked',
                                                                    item
                                                                )
                                                        "
                                                        :fields="fields"
                                                        :items="campaignpost.unranked"
                                                        :per-page="15"
                                                        :current-page="
                                                            campaignpost.currentPageUnranked
                                                        "
                                                        sortBy="minrank"
                                                        :sort-compare="customTableSort"
                                                        responsive="sm"
                                                        ref="selectableTable"
                                                    >
                                                        <template
                                                            v-slot:cell(selected)="{
                                                                item,
                                                                field: { key },
                                                            }"
                                                        >
                                                            <template v-if="item[key]">
                                                                <span aria-hidden="true"
                                                                    ><i
                                                                        class="
                                                                            far
                                                                            fa-check-square
                                                                            text-primary
                                                                            font-weight-bolder
                                                                        "
                                                                    ></i
                                                                ></span>
                                                                <span class="sr-only"
                                                                    >Selected</span
                                                                >
                                                            </template>
                                                            <template v-else>
                                                                <span aria-hidden="true"
                                                                    ><i class="far fa-square"></i
                                                                ></span>
                                                                <span class="sr-only"
                                                                    >Not selected</span
                                                                >
                                                            </template>
                                                            <!-- <b-checkbox v-model="item[key]"></b-checkbox> -->
                                                        </template>

                                                        <!-- Hashtag badge slot -->
                                                        <template
                                                            v-slot:cell(hashtagsBadged)="{
                                                                item,
                                                                field: { key },
                                                            }"
                                                        >
                                                            {{ item.hashtag }}
                                                            <span
                                                                class="badge badge-warning"
                                                                v-if="item.status == 'flagged'"
                                                                >Flagged</span
                                                            >
                                                            <span
                                                                class="badge badge-danger"
                                                                v-if="item.status == 'banned'"
                                                                >Banned</span
                                                            >
                                                        </template>
                                                    </b-table>
                                                    <b-pagination
                                                        v-model="campaignpost.currentPageUnranked"
                                                        :total-rows="campaignpost.unranked.length"
                                                        :per-page="15"
                                                        aria-controls="my-table"
                                                        align="center"
                                                    ></b-pagination>
                                                </div>
                                                <div v-else>
                                                    <InfoBox
                                                        text="There are no hashtags on which this post hasn't ranked"
                                                    ></InfoBox>
                                                </div>
                                            </b-tab>
                                            <b-tab title="Statistics" title-item-class="mytab">
                                                <div class="pt-3">
                                                    <!--begin::Item-->
                                                    <div
                                                        class="
                                                            d-flex
                                                            align-items-center
                                                            mb-1
                                                            mt-1
                                                            rounded
                                                            p-1
                                                        "
                                                    >
                                                        <!--begin::Icon-->
                                                        <span
                                                            class="svg-icon svg-icon-warning mr-5"
                                                        >
                                                            <span class="svg-icon svg-icon-lg">
                                                                <!--begin::Svg Icon | path:assets/media/svg/icons/Home/Library.svg-->
                                                                <i
                                                                    class="flaticon2-line-chart"
                                                                    :class="
                                                                        campaignpost.statistics
                                                                            .classes.engagementicon
                                                                    "
                                                                ></i>
                                                                <!--end::Svg Icon-->
                                                            </span>
                                                        </span>
                                                        <!--end::Icon-->
                                                        <!--begin::Title-->
                                                        <div
                                                            class="
                                                                d-flex
                                                                flex-column flex-grow-1
                                                                mr-2
                                                            "
                                                        >
                                                            <a
                                                                class="
                                                                    font-weight-bold
                                                                    text-dark-75 text-hover-primary
                                                                    font-size-lg
                                                                    mb-1
                                                                "
                                                                >Engagement</a
                                                            >
                                                            <span
                                                                class="text-muted font-weight-bold"
                                                                >{{
                                                                    campaignpost.statistics
                                                                        .descriptions.engagement
                                                                }}</span
                                                            >
                                                        </div>
                                                        <!--end::Title-->
                                                        <!--begin::Lable-->
                                                        <span
                                                            class="
                                                                font-weight-bolder
                                                                py-1
                                                                font-size-lg
                                                            "
                                                            :class="
                                                                campaignpost.statistics.classes
                                                                    .engagementicon
                                                            "
                                                            >{{
                                                                campaignpost.statistics.engagement
                                                            }}%</span
                                                        >
                                                        <!--end::Lable-->
                                                    </div>
                                                    <!--end::Item-->

                                                    <div
                                                        class="
                                                            separator
                                                            separator-dashed
                                                            separator-border-1
                                                        "
                                                    ></div>

                                                    <!--begin::Item-->
                                                    <div
                                                        class="
                                                            d-flex
                                                            align-items-center
                                                            rounded
                                                            p-1
                                                            mb-1
                                                            mt-1
                                                        "
                                                    >
                                                        <!--begin::Icon-->
                                                        <span
                                                            class="svg-icon svg-icon-success mr-5"
                                                        >
                                                            <span class="svg-icon svg-icon-lg">
                                                                <!--begin::Svg Icon | path:assets/media/svg/icons/Communication/Write.svg-->
                                                                <i
                                                                    class="fas fa-heart"
                                                                    :class="
                                                                        campaignpost.statistics
                                                                            .classes.likesicon
                                                                    "
                                                                ></i>
                                                                <!--end::Svg Icon-->
                                                            </span>
                                                        </span>
                                                        <!--end::Icon-->
                                                        <!--begin::Title-->
                                                        <div
                                                            class="
                                                                d-flex
                                                                flex-column flex-grow-1
                                                                mr-2
                                                            "
                                                        >
                                                            <a
                                                                class="
                                                                    font-weight-bold
                                                                    text-dark-75 text-hover-primary
                                                                    font-size-lg
                                                                    mb-1
                                                                "
                                                                >Likes</a
                                                            >
                                                            <span
                                                                class="text-muted font-weight-bold"
                                                                >{{
                                                                    campaignpost.statistics
                                                                        .descriptions.likes
                                                                }}</span
                                                            >
                                                        </div>
                                                        <!--end::Title-->
                                                        <!--begin::Lable-->
                                                        <span
                                                            class="
                                                                font-weight-bolder
                                                                py-1
                                                                font-size-lg
                                                            "
                                                            :class="
                                                                campaignpost.statistics.classes
                                                                    .likesicon
                                                            "
                                                            >{{
                                                                abbreviateNumber(
                                                                    campaignpost.statistics.likes
                                                                )
                                                            }}</span
                                                        >
                                                        <!--end::Lable-->
                                                    </div>
                                                    <!--end::Item-->

                                                    <div
                                                        class="
                                                            separator
                                                            separator-dashed
                                                            separator-border-1
                                                        "
                                                    ></div>

                                                    <!--begin::Item-->
                                                    <div
                                                        class="
                                                            d-flex
                                                            align-items-center
                                                            rounded
                                                            p-1
                                                            mb-1
                                                            mt-1
                                                        "
                                                    >
                                                        <!--begin::Icon-->
                                                        <span class="svg-icon svg-icon-danger mr-5">
                                                            <span class="svg-icon svg-icon-lg">
                                                                <!--begin::Svg Icon | path:assets/media/svg/icons/Communication/Group-chat.svg-->
                                                                <i
                                                                    class="fas fa-comments"
                                                                    :class="
                                                                        campaignpost.statistics
                                                                            .classes.commentsicon
                                                                    "
                                                                ></i>
                                                                <!--end::Svg Icon-->
                                                            </span>
                                                        </span>
                                                        <!--end::Icon-->
                                                        <!--begin::Title-->
                                                        <div
                                                            class="
                                                                d-flex
                                                                flex-column flex-grow-1
                                                                mr-2
                                                            "
                                                        >
                                                            <a
                                                                class="
                                                                    font-weight-bold
                                                                    text-dark-75 text-hover-primary
                                                                    font-size-lg
                                                                    mb-1
                                                                "
                                                                >Comments</a
                                                            >
                                                            <span
                                                                class="text-muted font-weight-bold"
                                                                >{{
                                                                    campaignpost.statistics
                                                                        .descriptions.comments
                                                                }}</span
                                                            >
                                                        </div>
                                                        <!--end::Title-->
                                                        <!--begin::Lable-->
                                                        <span
                                                            class="
                                                                font-weight-bolder
                                                                py-1
                                                                font-size-lg
                                                            "
                                                            :class="
                                                                campaignpost.statistics.classes
                                                                    .commentsicon
                                                            "
                                                            >{{
                                                                abbreviateNumber(
                                                                    campaignpost.statistics.comments
                                                                )
                                                            }}</span
                                                        >
                                                        <!--end::Lable-->
                                                    </div>
                                                    <!--end::Item-->

                                                    <div
                                                        class="
                                                            separator
                                                            separator-dashed
                                                            separator-border-1
                                                        "
                                                        v-if="campaignpost.nice_type == 'Video'"
                                                    ></div>

                                                    <!--begin::Item-->
                                                    <div
                                                        class="
                                                            d-flex
                                                            align-items-center
                                                            rounded
                                                            p-1
                                                            mb-1
                                                            mt-1
                                                        "
                                                        v-if="campaignpost.nice_type == 'Video'"
                                                    >
                                                        <!--begin::Icon-->
                                                        <span class="svg-icon svg-icon-info mr-5">
                                                            <span class="svg-icon svg-icon-lg">
                                                                <!--begin::Svg Icon | path:assets/media/svg/icons/General/Attachment2.svg-->
                                                                <i
                                                                    class="fas fa-eye text-primary"
                                                                ></i>
                                                                <!--end::Svg Icon-->
                                                            </span>
                                                        </span>
                                                        <!--end::Icon-->
                                                        <!--begin::Title-->
                                                        <div
                                                            class="
                                                                d-flex
                                                                flex-column flex-grow-1
                                                                mr-2
                                                            "
                                                        >
                                                            <a
                                                                class="
                                                                    font-weight-bold
                                                                    text-dark-75 text-hover-primary
                                                                    font-size-lg
                                                                    mb-1
                                                                "
                                                                >Views</a
                                                            >
                                                            <span
                                                                class="text-muted font-weight-bold"
                                                                >Views on this video post</span
                                                            >
                                                        </div>
                                                        <!--end::Title-->
                                                        <!--begin::Lable-->
                                                        <span
                                                            class="
                                                                font-weight-bolder
                                                                text-primary
                                                                py-1
                                                                font-size-lg
                                                            "
                                                            >{{
                                                                abbreviateNumber(
                                                                    campaignpost.statistics.views
                                                                )
                                                            }}</span
                                                        >
                                                        <!--end::Lable-->
                                                    </div>
                                                    <!--end::Item-->
                                                    <div
                                                        class="
                                                            separator
                                                            separator-dashed
                                                            separator-border-1
                                                        "
                                                    ></div>
                                                </div>

                                                <!-- Error management -->
                                                <CustomAlerts
                                                    :errors="campaignpost.errors"
                                                    class="gutter-t"
                                                ></CustomAlerts>

                                                <!-- Load detailed analytics -->
                                                <template v-if="!campaignpost.detailed">
                                                    <b-button
                                                        variant="outline-success"
                                                        class="btn-wide col-xl-4 mt-5"
                                                        :disabled="campaignpost.loadingStats"
                                                        :class="
                                                            campaignpost.loadingStats
                                                                ? 'spinner spinner-white spinner-left'
                                                                : ''
                                                        "
                                                        @click="
                                                            getDetailedAnalytics(
                                                                campaignpost,
                                                                postIndex
                                                            )
                                                        "
                                                    >
                                                        Load detailed analytics
                                                    </b-button>
                                                </template>

                                                <!-- Display detailed analytics -->
                                                <template v-if="campaignpost.detailed">
                                                    <div class="card card-custom gutter-t">
                                                        <div class="card-header">
                                                            <div class="card-title">
                                                                <h3 class="card-label">
                                                                    Detailed growth
                                                                </h3>
                                                            </div>
                                                        </div>
                                                        <div class="card-body">
                                                            <!--begin::Chart-->
                                                            <apexchart
                                                                :options="campaignpost.chartOptions"
                                                                :series="campaignpost.chartSeries"
                                                                type="area"
                                                            ></apexchart>
                                                            <!--end::Chart-->
                                                        </div>
                                                    </div>
                                                </template>
                                            </b-tab>
                                            <b-tab title="Caption" title-item-class="mytab">
                                                <div class="caption">
                                                    {{ campaignpost.caption }}
                                                </div>
                                            </b-tab>
                                        </b-tabs>
                                    </div>
                                    <!--end::Bottom-->
                                </div>
                            </div>
                        </template>
                        <div v-if="morePages">
                            <button
                                type="button"
                                class="btn btn-white btn-wide col-12 gutter-b d-print-none"
                                @click="getCampaignPosts(campaign.accountid, nextPage)"
                            >
                                <i class="fas fa-redo text-success"></i>
                                Load more results...
                            </button>
                        </div>
                    </div>

                    <PagePreloader v-if="postsAreLoading">posts</PagePreloader>
                </div>
            </div>
            <!-- Close col 9 -->
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "~bootstrap-vue/dist/bootstrap-vue.css";
.campaign-link {
    cursor: pointer;
}
.campaign-link-active {
    background-color: #f3f6f9;
}
.caption {
    padding: 1.5rem;
    border-left: 4px solid #e2e5ec;
    background-color: #f7f8fa;
    white-space: pre-line;
}
.account-bio {
    white-space: pre-line;
    width: 100%;
}
.result-title {
    color: #595d6e;
    padding-top: 20px;
    font-size: 1.1rem;
}
.result-desc {
    color: #74788d;
    font-weight: 400;
    font-size: 13px;
}
.result-value {
    display: flex;
    margin-left: 0.5rem;
}
.card-border-right {
    border-right: 1px solid #ebedf2;
}
.result-card {
    height: 100px;
    border-bottom: 1px solid #ebedf2;
}
.fade-leave-active {
    transition: all 1s;
}

.fade-leave-to {
    opacity: 0;
}
.card-header {
    min-height: 60px;
}
</style>
<style scoped>
.tabs /deep/ .tab-pane:focus {
    outline: none;
}
.tabs /deep/ .mytab > .active {
    background-color: white;
    color: #1dc9b7;
    font-weight: 600;
}
.tabs /deep/ .mytab > .active a {
    border-bottom: 3px;
}
.tabs /deep/ .nav-pills {
    border-style: solid;
    border-top: 0;
    border-left: 0;
    border-right: 0;
    border-color: rgba(29, 201, 183, 0.1);
}
</style>

<script>
// Defaults
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import axios from "axios";
// Components
import PagePreloader from "@/view/content/Custom/PagePreloader.vue";
import CustomAlerts from "@/view/content/Custom/CustomAlerts.vue";
import AccountBadgeWide from "@/view/content/Custom/AccountBadgeWide.vue";
import InfoBox from "@/view/content/Custom/InfoBox.vue";
import EnterpriseLogoDisplay from "@/view/content/Custom/EnterpriseLogoDisplay.vue";
// Buttons
import buttonAddToExistingHashtagSet from "@/view/content/Custom/buttonAddToExistingHashtagSet.vue";
import buttonCreateNewHashtagSet from "@/view/content/Custom/buttonCreateNewHashtagSet.vue";
import buttonCopyToClipboard from "@/view/content/Custom/buttonCopyToClipboard.vue";
// Mixins
import stringManagement from "@/mixins/stringManagement";
import hashtagTableManagement from "@/mixins/hashtagTableManagement";
import axiosErrorManagement from "@/mixins/axiosErrorManagement";

export default {
    name: "MyCampaigns",
    mixins: [stringManagement, hashtagTableManagement, axiosErrorManagement],
    components: {
        PagePreloader,
        AccountBadgeWide,
        InfoBox,
        CustomAlerts,
        buttonAddToExistingHashtagSet,
        buttonCreateNewHashtagSet,
        buttonCopyToClipboard,
        EnterpriseLogoDisplay,
    },
    props: ["propUserid"],
    data() {
        return {
            // Preloaders
            pageIsLoaded: false,
            campaignIsLoading: false,
            postsAreLoading: false,
            modalIsLoading: false,

            // Displayers
            displayCampaign: false,
            displayPosts: false,

            // Data campaigns
            mycampaigns: [],
            campaign: [],
            currentcampaign: "",
            summaryTabIndex: 0,
            canDeleteCampaign: false,

            // Data campaign posts
            campaignposts: [],
            morePages: false,
            nextPage: 1,

            // Selected hashtags
            selectedHashtags: [],
            allSelectedHashtags: [],

            // New hashtag set data
            modalerrors: [],

            // Add Campaign
            formAddAccount: "",

            // Global table variables
            perPage: 5,
            currentPage: 1,
            summaryCurrentPage: 1,

            fieldsSummary: [
                {
                    key: "selected",
                    label: "",
                },
                {
                    key: "hashtag",
                    sortable: true,
                    tdClass: "d-none",
                    thClass: "d-none",
                },
                {
                    key: "hashtagsBadged",
                    sortable: true,
                    label: "Hashtags",
                },
                {
                    key: "usedcount",
                    sortable: true,
                    label: "Number of times used",
                },
                {
                    key: "top9count",
                    sortable: true,
                    label: "Number of times you ranked in top9",
                },
                {
                    key: "performanceTop9",
                    sortable: true,
                    label: "Top9 Performance",
                    sortByFormatted: true,
                    formatter: (value, key, item) => {
                        if (item.top9count < 1) {
                            return "0%";
                        }
                        return Math.round((item.top9count / item.usedcount) * 100, 2) + "%";
                    },
                },
                {
                    key: "rankcount",
                    sortable: true,
                    label: "Number of times you ranked in top100",
                },
                {
                    key: "performance100",
                    sortable: true,
                    label: "Top100 Performance",
                    sortByFormatted: true,
                    formatter: (value, key, item) => {
                        if (item.rankcount < 1) {
                            return "0%";
                        }
                        return Math.round((item.rankcount / item.usedcount) * 100, 2) + "%";
                    },
                },
                {
                    key: "lowestrank",
                    sortable: true,
                    label: "Best rank",
                    formatter: (value, key, item) => {
                        return this.makeOrdinal(value);
                    },
                },
                {
                    key: "highestrank",
                    sortable: true,
                    label: "Worst rank",
                    formatter: (value, key, item) => {
                        return this.makeOrdinal(value);
                    },
                },
            ],

            // Fields for Ranked / Unranked
            fields: [
                {
                    key: "selected",
                    label: "",
                },
                {
                    key: "hashtag",
                    sortable: true,
                    tdClass: "d-none",
                    thClass: "d-none",
                },
                {
                    key: "hashtagsBadged",
                    sortable: true,
                    label: "Hashtags",
                },
                {
                    key: "minrank",
                    sortable: true,
                    label: "Rank",
                    formatter: (value, key, item) => {
                        return this.makeOrdinal(value);
                    },
                },
                {
                    key: "count",
                    sortable: true,
                    label: "Post count",
                    formatter: (value) => {
                        if (value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(value);
                    },
                },
                {
                    key: "DAPC_weekly",
                    sortable: true,
                    label: "Daily Posts",
                    formatter: (value) => {
                        if (value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(Math.round(value));
                    },
                },
                {
                    key: "score",
                    sortable: true,
                    label: "Score",
                },
                {
                    key: "avgMaxLikes",
                    sortable: true,
                    label: "Max Likes in Top 9",
                    formatter: (value) => {
                        if (value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(value);
                    },
                },
                {
                    key: "avgMinLikes",
                    sortable: true,
                    label: "Min Likes in Top 9",
                    formatter: (value) => {
                        if (value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(value);
                    },
                },
                {
                    key: "avgAvgLikes",
                    sortable: true,
                    label: "Average Likes in Top 9",
                    formatter: (value) => {
                        if (value === null) {
                            return 0;
                        }
                        return this.addCommaToNumber(value);
                    },
                },
            ],

            // Charts
            campaignChartSeries: [],
            campaignChartLabels: [],
            campaignChartOptions: {},

            // Error handling
            errors: [],

            // Fedor custom code
            sortCampaignsMode: 1,
            sortCampaignsOptions: [
                { value: 1, text: "Alphabetically (A->Z)" },
                { value: 2, text: "Alphabetically (Z->A)" },
                { value: 3, text: "Smallest to biggest following" },
                { value: 4, text: "Biggest to smallest following" },
                { value: 5, text: "Youngest to oldest Campaign" },
                { value: 6, text: "Oldest to youngest Campaign" },
            ],
        };
    },
    methods: {
        onRowClicked(tableid, item) {
            // Hack
            if (item.selected) {
                this.$set(item, "selected", false);
            } else {
                this.$set(item, "selected", true);
            }

            // Get selected items
            this.getAllSelectedItems();
        },

        getAllSelectedItems() {
            // Get variable to store items
            var hSelection = [];

            // Generate array of all selected hashtags by table
            // Get all hashtags from campaign posts
            this.campaignposts.forEach((post) => {
                // Do ranked hashtags
                post.rank_data.forEach((item) => {
                    if (item.selected == true) {
                        hSelection.push(item.hashtag);
                    }
                });

                // Do ranked hashtags
                post.unranked.forEach((item) => {
                    if (item.selected == true) {
                        hSelection.push(item.hashtag);
                    }
                });
            });

            // Get selected hashtags from summary table
            this.campaign.performance.individual_hashtag_performance.forEach((item) => {
                if (item.selected == true) {
                    hSelection.push(item.hashtag);
                }
            });

            // Remove duplicates
            hSelection = [...new Set(hSelection)];

            // Return variable
            this.selectedHashtags = hSelection;
        },

        loopThroughTableAndSet(variant, value, postIndex = null) {
            if (variant == "summary") {
                if (this.campaign.performance.individual_hashtag_performance.length > 0) {
                    this.campaign.performance.individual_hashtag_performance.forEach(
                        (part, index, theArray) => {
                            this.$set(
                                this.campaign.performance.individual_hashtag_performance[index],
                                "selected",
                                value
                            );
                        }
                    );
                }
            } else if (variant == "ranked") {
                if (this.campaignposts[postIndex].rank_data.length > 0) {
                    this.campaignposts[postIndex].rank_data.forEach((part, index, theArray) => {
                        this.$set(
                            this.campaignposts[postIndex].rank_data[index],
                            "selected",
                            value
                        );
                    });
                }
            } else if (variant == "unranked") {
                if (this.campaignposts[postIndex].unranked.length > 0) {
                    this.campaignposts[postIndex].unranked.forEach((part, index, theArray) => {
                        this.$set(this.campaignposts[postIndex].unranked[index], "selected", value);
                    });
                }
            }
        },

        selectAll(variant, index = null) {
            this.loopThroughTableAndSet(variant, true, index);
            this.getAllSelectedItems();
        },

        deselectAll(variant, index = null) {
            this.loopThroughTableAndSet(variant, false, index);
            this.getAllSelectedItems();
        },

        onRowSelected(tableid, items) {
            // Get variable to store items
            var hSelection = [];

            // Generate array of all selected hashtags by table
            this.allSelectedHashtags[tableid] = { items };

            // Loop through all the tables
            Object.keys(this.allSelectedHashtags).forEach((key, value) => {
                // Loop through all the selected rows from each table
                this.allSelectedHashtags[key].items.forEach((item) => {
                    hSelection.push(item.hashtag);
                });
            });

            // Remove duplicates
            hSelection = [...new Set(hSelection)];

            // Return variable
            this.selectedHashtags = hSelection;
        },

        getAllCampaigns() {
            axios
                .get("campaigns")
                .then((response) => {
                    // Set data
                    this.mycampaigns = response.data.data;

                    // If we have a campaign, get the first one
                    if (this.mycampaigns.length > 0) {
                        // Only if we don't have a prop, if not it will conflict
                        // CASE: Clicked on "View Campaign" from Account Scanner is overridden by the default behaviour
                        if (typeof this.propUserid !== "undefined") {
                        } else {
                            this.getCampaign(this.mycampaigns[0]);
                        }
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Close preloader
                    this.pageIsLoaded = true;
                });
        },

        getCampaign(campaign) {
            // Set preloader
            this.campaignIsLoading = true;
            this.postsAreLoading = true;

            // Reset displayers
            this.displayCampaign = false;
            this.displayPosts = false;

            // Reset all errors
            this.errors = [];

            // Reset the current campaign
            this.campaign = [];
            this.currentcampaign = "";
            this.canDeleteCampaign = false;

            // Reset the summary tab index
            this.summaryTabIndex = 0;

            // Reset the selected hashtags
            this.selectedHashtags = [];
            this.allSelectedHashtags = [];

            // Reset the campaign posts
            this.campaignposts = [];
            this.morePages = false;
            this.nextPage = 1;

            // Get the campaign and performance
            axios
                .get("campaigns/" + campaign.accountid)
                .then((response) => {
                    // Handle respones
                    if (response.data.status == "OK") {
                        // Make pretty
                        this.campaign = response.data.data;

                        // Set current campaign ID
                        this.currentcampaign = this.campaign.accountid;

                        // Check if we can delete the campaign
                        // CASE - Campaign older than 5 days or newer than 2 hours
                        if (
                            this.campaign.progress.dayssincestart >= 5 ||
                            this.campaign.progress.hourssincestart <= 2
                        ) {
                            this.canDeleteCampaign = true;
                        }

                        // Get statistics for graphs
                        var statistics = this.campaign.performance.statistics;

                        // Define progress chart data
                        this.campaignChartSeries = [
                            statistics.totaltop9hashtags,
                            statistics.count10to25,
                            statistics.count26to50,
                            statistics.count51to75,
                            statistics.count76to100,
                            statistics.totalunrankedhashtags,
                        ];
                        this.campaignChartLabels = [
                            "Top 9",
                            "Top 10 to 25",
                            "Top 26 to 50",
                            "Top 51 to 75",
                            "Top 76 to 100",
                            "Unranked",
                        ];
                        this.campaignChartOptions = {
                            labels: this.campaignChartLabels,
                            colors: [
                                "#1dc9b7",
                                "#366cf3",
                                "#5867dd",
                                "#36a3f7",
                                "#ffb822",
                                "#fd397a",
                            ],
                            chart: {
                                type: "donut",
                                animations: {
                                    enabled: true,
                                    easing: "easeinout",
                                    speed: 800,
                                    animateGradually: {
                                        enabled: true,
                                        delay: 150,
                                    },
                                    dynamicAnimation: {
                                        enabled: true,
                                        speed: 350,
                                    },
                                },
                            },
                            responsive: [
                                {
                                    breakpoint: 480,
                                    options: {
                                        chart: {
                                            height: 300,
                                        },
                                        legend: {
                                            position: "bottom",
                                        },
                                    },
                                },
                            ],
                            plotOptions: {
                                pie: {
                                    donut: {
                                        labels: {
                                            show: true,
                                        },
                                    },
                                },
                            },
                            legend: {
                                verticalAlign: "middle",
                            },
                        };

                        // Display
                        this.displayCampaign = true;

                        // Get campaign posts for page 1
                        this.getCampaignPosts(this.campaign.accountid, 1);
                    }

                    if (response.data.message !== undefined) {
                        this.errors.push({
                            type: response.data.message.type,
                            message: response.data.message.message,
                        });
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                    this.postsAreLoading = false;
                })
                .finally(() => {
                    // Remove preloader
                    this.campaignIsLoading = false;
                });
        },

        getCampaignPosts(accountid, page) {
            // Set preloaders
            this.postsAreLoading = true;

            // Reset morePages
            this.morePages = false;

            // Get the campaign posts
            axios
                .get("campaigns/" + accountid + "/posts?page=" + page)
                .then((response) => {
                    // Handle respones
                    if (response.data.status == "OK") {
                        // Get the actual posts
                        //                        this.campaignposts = response.data.data.posts.data;
                        this.campaignposts = this.campaignposts.concat(
                            response.data.data.posts.data
                        );

                        // Declare campaign variable
                        var campaign = this.campaign;

                        // Add elements to the campaign posts array
                        this.campaignposts.forEach(function (part, index, theArray) {
                            // Calculate engagement
                            // Check if we actually have followers
                            theArray[index].statistics.engagement = 0;
                            if (campaign.account_data.followers > 0) {
                                theArray[index].statistics.engagement = Math.round(
                                    (theArray[index].statistics.likes /
                                        campaign.account_data.followers) *
                                        100,
                                    2
                                );
                            }

                            // Generate defaults
                            var colorsuccess = "bg-light-success";
                            var colorwarning = "bg-light-warning";
                            var colordanger = "bg-light-danger";
                            var iconsuccess = "text-success";
                            var iconwarning = "text-warning";
                            var icondanger = "text-danger";

                            // Generate descriptions for posts
                            /*
                             * ENGAGEMENT
                             */
                            theArray[index].statistics.classes = {};
                            theArray[index].statistics.descriptions = {};
                            if (
                                theArray[index].statistics.engagement >=
                                campaign.account_data.engagement
                            ) {
                                theArray[index].statistics.classes.engagement = colorsuccess;
                                theArray[index].statistics.classes.engagementicon = iconsuccess;
                                theArray[index].statistics.descriptions.engagement =
                                    "Engagement on this post is higher than usual.";
                            } else if (
                                theArray[index].statistics.engagement >=
                                campaign.account_data.engagement * 100 * 0.9
                            ) {
                                theArray[index].statistics.classes.engagement = colorwarning;
                                theArray[index].statistics.classes.engagementicon = iconwarning;
                                theArray[index].statistics.descriptions.engagement =
                                    "Engagement on this posts is close to usual.";
                            } else {
                                theArray[index].statistics.classes.engagement = colordanger;
                                theArray[index].statistics.classes.engagementicon = icondanger;
                                theArray[index].statistics.descriptions.engagement =
                                    "This post currently has lower engagement than your average post.";
                            }

                            // Check color for likes
                            if (
                                theArray[index].statistics.likes >= campaign.account_data.avglikes
                            ) {
                                theArray[index].statistics.classes.likes = colorsuccess;
                                theArray[index].statistics.classes.likesicon = iconsuccess;
                                theArray[index].statistics.descriptions.likes =
                                    "This post is performing better than usual, with " +
                                    (theArray[index].statistics.likes -
                                        campaign.account_data.avglikes) +
                                    " more likes than your average.";
                            } else if (
                                theArray[index].statistics.likes >=
                                campaign.account_data.avglikes * 0.9
                            ) {
                                theArray[index].statistics.classes.likes = colorwarning;
                                theArray[index].statistics.classes.likesicon = iconwarning;
                                theArray[index].statistics.descriptions.likes =
                                    "This post is performing close to usual.";
                            } else {
                                theArray[index].statistics.classes.likes = colordanger;
                                theArray[index].statistics.classes.likesicon = icondanger;
                                theArray[index].statistics.descriptions.likes =
                                    "This post currently has less likes than your average post.";
                            }

                            // Check color for comments
                            if (
                                theArray[index].statistics.comments >=
                                campaign.account_data.avgcomments
                            ) {
                                theArray[index].statistics.classes.comments = colorsuccess;
                                theArray[index].statistics.classes.commentsicon = iconsuccess;
                                theArray[index].statistics.descriptions.comments =
                                    "This post is performing better than usual, with " +
                                    (theArray[index].statistics.comments -
                                        campaign.account_data.avgcomments) +
                                    " more comments than your average";
                            } else if (
                                theArray[index].statistics.comments >=
                                campaign.account_data.avgcomments * 0.9
                            ) {
                                theArray[index].statistics.classes.comments = colorwarning;
                                theArray[index].statistics.classes.commentsicon = iconwarning;
                                theArray[index].statistics.descriptions.comments =
                                    "This post is performing close to usual";
                            } else {
                                theArray[index].statistics.classes.comments = colordanger;
                                theArray[index].statistics.classes.commentsicon = icondanger;
                                theArray[index].statistics.descriptions.comments =
                                    "This post currently has less comments than your average post";
                            }
                        });

                        // Check if we have more pages
                        if (
                            response.data.data.posts.current_page <
                            response.data.data.posts.last_page
                        ) {
                            this.morePages = true;
                            this.nextPage = response.data.data.posts.current_page + 1;
                        }
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Reset loaders
                    this.postsAreLoading = false;
                    this.displayPosts = true;
                });
        },

        getDetailedAnalytics(post, index) {
            // Set loader
            this.$set(this.campaignposts[index], "loadingStats", true);

            // Perform query
            axios
                .post("campaigns/posts/stats", {
                    postid: post.postid,
                })
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check status
                    if (data.status == "OK") {
                        // Enable chart
                        this.$set(this.campaignposts[index], "detailed", data.data);

                        /**
                         * SECTION - Handle growth chart
                         */

                        // Prepare daily growth chart
                        var chartLikes = [];
                        var chartComments = [];
                        var chartDates = [];
                        data.data.forEach((item) => {
                            chartLikes.push(item.likes);
                            chartComments.push(item.comments);
                            chartDates.push(item.datetime);
                        });

                        // Prepare chart series
                        var chartSeries = [
                            {
                                name: "Likes",
                                data: chartLikes,
                            },
                            {
                                name: "Comments",
                                data: chartComments,
                            },
                        ];

                        // Prepare chart options
                        var chartOptions = {
                            chart: {
                                type: "area",
                                height: "auto",
                                zoom: {
                                    enabled: false,
                                },
                            },
                            dataLabels: {
                                enabled: false,
                            },
                            stroke: {
                                curve: "straight",
                            },
                            labels: chartDates,
                            xaxis: {
                                type: "datetime",
                            },
                            yaxis: {
                                opposite: true,
                            },
                            legend: {
                                horizontalAlign: "left",
                            },
                        };

                        // Assign chart data
                        this.$set(this.campaignposts[index], "chartSeries", chartSeries);
                        this.$set(this.campaignposts[index], "chartOptions", chartOptions);
                    }

                    // Handle errors
                    if (data.messages.length > 0) {
                        this.$set(this.campaignposts[index], "errors", [data.messages[0]]);
                    }
                })
                .catch((error) => {
                    this.$set(this.campaignposts[index], "errors", [this.handleAxiosErrors(error)]);
                })
                .finally(() => {
                    // Stop loader
                    this.$set(this.campaignposts[index], "loadingStats", false);
                });
        },

        openNewCampaignModal() {
            // Reset errors
            this.modalerrors = [];
            this.modalIsLoading = false;

            // Open modal
            this.$refs["add-account-modal"].show();
        },

        addCampaign() {
            // Reset errors
            this.modalerrors = [];
            this.modalIsLoading = true;

            // Perform post
            axios
                .post("campaigns/new", {
                    account: this.formAddAccount,
                })
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Handle response
                    if (data.status == "OK") {
                        // Add success message
                        if (data.messages.length > 0) {
                            data.messages.forEach((item) => {
                                this.errors.push({
                                    type: item.type,
                                    message: item.message,
                                });
                            });
                        }

                        // Add campaign to array
                        var campaign = data.data;
                        this.mycampaigns.unshift(campaign);

                        // Switch to new campaign
                        this.getCampaign(campaign);

                        // Add success message
                        if (data.messages.length > 0) {
                            data.messages.forEach((item) => {
                                this.errors.push({
                                    type: item.type,
                                    message: item.message,
                                });
                            });
                        }

                        // Reset text input
                        this.formAddAccount = "";

                        // Close modal
                        this.$refs["add-account-modal"].hide();
                    }

                    // Handle errors
                    if (data.messages.length > 0) {
                        data.messages.forEach((item) => {
                            this.modalerrors.push({
                                type: item.type,
                                message: item.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.modalerrors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Turn preloader off
                    this.modalIsLoading = false;
                });
        },

        openStopCampaignModal() {
            // Reset errors
            this.modalerrors = [];
            this.modalIsLoading = false;

            // Open modal
            this.$refs["delete-account-modal"].show();
        },

        stopCampaign() {
            // Reset errors
            this.modalerrors = [];
            this.modalIsLoading = true;

            // Make pretty
            var accountid = this.campaign.account_data.userid;

            // Perform POST
            axios
                .post("campaigns/delete", {
                    accountid: accountid,
                })
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check status
                    if (data.status == "OK") {
                        // Add success message
                        if (data.messages.length > 0) {
                            data.messages.forEach((item) => {
                                this.errors.push({
                                    type: item.type,
                                    message: item.message,
                                });
                            });
                        }

                        // Remove campaign
                        var key;
                        this.mycampaigns.forEach((value, index) => {
                            if (value.accountid == accountid) {
                                key = index;
                            }
                        });
                        this.mycampaigns.splice(key, 1);

                        // Back to main screen
                        this.displayCampaign = false;

                        // Close modal
                        this.$refs["delete-account-modal"].hide();
                    }

                    // Add error message
                    if (data.messages.length > 0) {
                        data.messages.forEach((item) => {
                            this.modalerrors.push({
                                type: item.type,
                                message: item.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Reset loaders
                    this.modalIsLoading = false;
                });
        },

        handleEmitMessages(messages) {
            if (messages.length > 0) {
                messages.forEach((currentItem) => {
                    this.errors.push({ type: currentItem.type, message: currentItem.message });
                });
            }
        },

        isCurrentCampaign(campaign) {
            if (campaign.accountid == this.currentcampaign) {
                return true;
            }
        },

        viewGrowth(username) {
            this.$router.push({
                name: "accountscanner",
                params: { propAccount: username },
            });
        },

        handleProp() {
            // Check prop
            if (typeof this.propUserid !== "undefined") {
                var obj = {
                    accountid: this.propUserid,
                };
                this.getCampaign(obj);
            }
        },

        printReport(accountid) {
            // Perform query
            axios
                .post("enterprise/exportreport", {
                    accountid: accountid,
                    accounttype: "campaign",
                })
                .then((response) => {
                    // Do nothing
                })
                .catch((error) => {
                    // Do nothing
                })
                .finally(() => {
                    // Close preloader
                    window.print();
                });
        },

        handleSortCampaignsChange() {
            // 1 - Alphabetically A-Z
            if (this.sortCampaignsMode == 1) {
                this.mycampaigns.sort(function (a, b) {
                    if (a.username < b.username) {
                        return -1;
                    }
                    if (a.username > b.username) {
                        return 1;
                    }
                    return 0;
                });
                // 2 - Alphabetically Z-A
            } else if (this.sortCampaignsMode == 2) {
                this.mycampaigns.sort(function (a, b) {
                    if (a.username < b.username) {
                        return 1;
                    }
                    if (a.username > b.username) {
                        return -1;
                    }
                    return 0;
                });
                // 3 - Smallest to biggest following
            } else if (this.sortCampaignsMode == 3) {
                this.mycampaigns.sort(function (a, b) {
                    if (a.followers < b.followers) {
                        return -1;
                    }
                    if (a.followers > b.followers) {
                        return 1;
                    }
                    return 0;
                });
                // 4 - Biggest to smallest following
            } else if (this.sortCampaignsMode == 4) {
                this.mycampaigns.sort(function (a, b) {
                    if (a.followers < b.followers) {
                        return 1;
                    }
                    if (a.followers > b.followers) {
                        return -1;
                    }
                    return 0;
                });
                // 5 - Youngest to oldest campaign
            } else if (this.sortCampaignsMode == 5) {
                this.mycampaigns.sort(function (a, b) {
                    if (a.days_since_start < b.days_since_start) {
                        return -1;
                    }
                    if (a.days_since_start > b.days_since_start) {
                        return 1;
                    }
                    return 0;
                });
                // 6 - Oldest to youngest campaign
            } else if (this.sortCampaignsMode == 6) {
                this.mycampaigns.sort(function (a, b) {
                    if (a.days_since_start < b.days_since_start) {
                        return 1;
                    }
                    if (a.days_since_start > b.days_since_start) {
                        return -1;
                    }
                    return 0;
                });
            }
        },
    },

    computed: {
        currentUser() {
            return this.$store.getters["auth/currentUser"];
        },
        isEnterprise() {
            return this.$store.getters["auth/isEnterprise"];
        },
        isFedor() {
            if (this.currentUser.id == 3 || this.currentUser.id == 26410) {
                return true;
            }
            return false;
        },
        selectedHashtagsHashed() {
            return "#" + this.selectedHashtags.join(" #");
        },

        canAddCampaigns() {
            if (
                typeof this.mycampaigns === "undefined" ||
                this.currentUser.campaigns === "undefined"
            ) {
                return false;
            } else {
                if (this.mycampaigns.length >= this.currentUser.campaigns) {
                    return false;
                } else {
                    return true;
                }
            }
        },
    },

    mounted() {
        this.getAllCampaigns();
        this.handleProp();
        this.$store.dispatch(SET_BREADCRUMB, [
            { title: "My Hashtag Performance Campaigns", route: "mycampaigns" },
        ]);
    },
};
</script>
